import type { RoleAssignmentRequest, UpdateUserRequest, UserDetailResponse } from '../api/generated';
import { sortRoles, type LocationRole } from '../constants/locationRoles';

// The Users page assigns locations to a user; the locations page assigns users
// to a location. Both directions read and write the same `roleAssignments` on
// the user, with the same roles per row, so neither page can show an assignment
// the other would contradict.
//
// The two are not equally careful about untouched data, though: saving on the
// Users page rebuilds the whole assignment list from the locations it happens to
// be showing, so it drops entries for any location absent from that list. This
// direction instead copies every other location's entry through verbatim.
//
// A user counts as assigned to a location when they hold at least one role
// there — which is also what decides whether the location is listed for them at
// all. Clearing every role removes the assignment entirely, which is what the
// Users page does too.

export interface LocationUserRoles {
  userId: string;
  roles: LocationRole[];
}

export const hasAnyRole = (assignment: LocationUserRoles): boolean => assignment.roles.length > 0;

export const countAssignedUsers = (assignments: LocationUserRoles[]): number =>
  assignments.filter(hasAnyRole).length;

/**
 * The request shape for one location's roles.
 *
 * The generated client types `roles` as a `Set` because the schema marks it
 * unique, but the body is JSON.stringify'd — and a real Set serializes to `{}`,
 * which would silently send no roles at all. So a plain array is cast through
 * `unknown`, which is the only shape that both type-checks and transmits.
 */
export const toRoleAssignmentRequest = (
  locationId: string,
  roles: readonly LocationRole[]
): RoleAssignmentRequest => ({
  locationId,
  roles: sortRoles(roles) as unknown as Set<LocationRole>
});

// Responses carry roles as a real array, but the generated type says otherwise
// for the request side of the same field; reading through Array.from copes with
// either.
export const readRoles = (roles: Iterable<LocationRole>): LocationRole[] =>
  sortRoles(Array.from(roles));

// Ticking a role box adds it, unticking removes it. Kept out of the two form
// modals that call it: written inline, it sat five callbacks deep inside their
// state updaters, and both directions of the same assignment have to agree on
// what a toggle means.
export const withRoleToggled = (
  roles: readonly LocationRole[],
  role: LocationRole
): LocationRole[] => (roles.includes(role) ? roles.filter((held) => held !== role) : [...roles, role]);

/**
 * One row per user, carrying the roles they currently hold at this location.
 * Users with no assignment come back with an empty role list, so the grid always
 * lists everyone and the administrator can assign roles without leaving the
 * page.
 */
export const getLocationUserRoles = (
  users: UserDetailResponse[],
  locationId: string
): LocationUserRoles[] =>
  users.map((user) => {
    const existing = user.roleAssignments.find((assignment) => assignment.locationId === locationId);
    return {
      userId: user.userId,
      roles: existing ? readRoles(existing.roles) : []
    };
  });

const hasSameRoles = (a: LocationUserRoles, b: LocationUserRoles): boolean => {
  const left = sortRoles(a.roles);
  const right = sortRoles(b.roles);
  return left.length === right.length && left.every((role, index) => role === right[index]);
};

const toRoleAssignmentsPayload = (
  user: UserDetailResponse,
  locationId: string,
  next: LocationUserRoles
): UpdateUserRequest['roleAssignments'] => {
  const others = user.roleAssignments
    .filter((assignment) => assignment.locationId !== locationId)
    .map((assignment) => toRoleAssignmentRequest(assignment.locationId, readRoles(assignment.roles)));

  // Every role cleared: drop the entry rather than storing an empty one.
  if (!hasAnyRole(next)) return others;

  return [...others, toRoleAssignmentRequest(locationId, next.roles)];
};

export interface LocationAssignmentUpdate {
  userId: string;
  payload: UpdateUserRequest;
}

/**
 * Thrown when some role writes landed and others did not. Assignments are saved
 * one user at a time, so a failure part-way through is a real state the operator
 * has to be told about precisely: reporting it as a plain "could not save" would
 * leave them believing nothing changed while some users already hold new roles.
 *
 * Retrying is safe. The next save re-diffs against refreshed user state, so only
 * the users that did not land are sent again.
 */
export class LocationAssignmentError extends Error {
  constructor(
    readonly failedUserNames: string[],
    readonly savedCount: number,
    // The first write's rejection, kept so the UI can show a server-provided
    // reason next to the who/how-many arithmetic — without it a permanent
    // failure (403) and a transient one (network) read as the same "try again".
    readonly firstFailureCause: unknown = undefined
  ) {
    super(`Failed to save roles for ${failedUserNames.length} user(s)`);
    this.name = 'LocationAssignmentError';
  }
}

/**
 * The create flow's partial failure: the location POST succeeded and the
 * location exists, but some of the role writes that followed did not.
 *
 * Carries the new location's id because the caller cannot recover without it —
 * the form has to switch from creating to editing that location, so a retry
 * writes the outstanding roles instead of POSTing a second location.
 */
export class LocationCreatedWithFailedRightsError extends LocationAssignmentError {
  constructor(
    readonly createdLocationId: string,
    failedUserNames: string[],
    savedCount: number,
    firstFailureCause: unknown = undefined
  ) {
    super(failedUserNames, savedCount, firstFailureCause);
    this.name = 'LocationCreatedWithFailedRightsError';
  }
}

export interface LocationAssignmentOutcome {
  failedUserIds: string[];
  savedCount: number;
  // The first rejection, so the caller can show a server-provided reason.
  firstFailureCause: unknown;
}

/**
 * Writes the role updates **one at a time**.
 *
 * Sequential on purpose, not for politeness. Saving a user's role assignments on
 * the backend is a read-modify-write over the whole account aggregate: it loads
 * every user's assignments, changes the one it was asked about, and writes them
 * all back (PermissionsService.replaceUserPermissions). Fire two of those at
 * once and the second overwrites the first, so assigning roles to two users in
 * one save silently loses one of them. Reproduced in the browser: two users
 * changed in a single save, only one survived a refresh.
 *
 * Serialising means each write reads the state the previous one committed. The
 * real fix belongs on the backend; until then this is what keeps the save
 * truthful.
 *
 * Every update is still attempted even after one fails, so a single rejection
 * cannot leave the rest silently unwritten.
 */
export const applyLocationAssignmentUpdates = async (
  updates: LocationAssignmentUpdate[],
  write: (userId: string, payload: UpdateUserRequest) => Promise<void>
): Promise<LocationAssignmentOutcome> => {
  const failedUserIds: string[] = [];
  let firstFailureCause: unknown = undefined;
  // Tracked separately rather than testing `firstFailureCause === undefined`,
  // which cannot tell "no failure yet" from "a rejection whose value IS
  // undefined" — that would report a later failure's cause as the first, so a
  // transient fault could be read as the permanent one beside it.
  let hasFailureCause = false;

  for (const update of updates) {
    try {
      await write(update.userId, update.payload);
    } catch (e) {
      console.error(`Failed to save location roles for user ${update.userId}:`, e);
      failedUserIds.push(update.userId);
      if (!hasFailureCause) {
        hasFailureCause = true;
        firstFailureCause = e;
      }
    }
  }

  return {
    failedUserIds,
    savedCount: updates.length - failedUserIds.length,
    firstFailureCause
  };
};

/**
 * Diffs the edited rights grid against what the backend already has and returns
 * one update per user whose roles at this location actually changed. Users the
 * administrator left alone are never re-sent, which keeps the save small and
 * avoids clobbering concurrent edits made on the Users page.
 */
export const buildLocationAssignmentUpdates = (
  users: UserDetailResponse[],
  locationId: string,
  nextAssignments: LocationUserRoles[]
): LocationAssignmentUpdate[] => {
  // Keyed off the same `users` array the diff iterates, so every user is present.
  const currentByUserId = new Map(
    getLocationUserRoles(users, locationId).map((assignment) => [assignment.userId, assignment] as const)
  );

  return users
    .map((user) => ({
      user,
      current: currentByUserId.get(user.userId),
      next: nextAssignments.find((assignment) => assignment.userId === user.userId)
    }))
    .filter(
      (
        entry
      ): entry is {
        user: UserDetailResponse;
        current: LocationUserRoles;
        next: LocationUserRoles;
      } =>
        // A user missing from the submitted grid is left exactly as the backend
        // has them, which is what stops this save from clobbering a concurrent
        // edit on the Users page.
        entry.current !== undefined &&
        entry.next !== undefined &&
        !hasSameRoles(entry.current, entry.next)
    )
    .map(({ user, next }) => ({
      userId: user.userId,
      payload: {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        // Carried through untouched: this page edits roles at one location and
        // has no business changing whether the user administers the account.
        accountAdmin: user.accountAdmin,
        roleAssignments: toRoleAssignmentsPayload(user, locationId, next)
      }
    }));
};
